import { useEffect, useRef } from 'react';
import { Link } from '@inertiajs/react';
import { CircleCheck, ShoppingCart, Share2, Zap, Code2, WandSparkles } from 'lucide-react';
import { route } from '../../utils/route';
import type { ShoppingListResponse, SmartListCartItem } from '../../types/smartList';

interface SuccessResultProps {
  result: ShoppingListResponse;
  isLocal: boolean;
  onShare: () => void;
  onRemoveItem: (index: number) => void;
  onNewList: () => void;
}

function TrashIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function formatPrice(price: number): string {
  return price.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function SuccessResult({ result, isLocal, onShare, onRemoveItem, onNewList }: SuccessResultProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const itemCount = result.cart.length;
  const itemLabel = itemCount === 1 ? 'producto agregado' : 'productos agregados';

  return (
    <div ref={containerRef} className="mt-5">
      {/* Success header */}
      <div className="bg-green-50 border-l-4 border-green-500 p-5 rounded-md mb-4">
        <h4 className="flex items-center gap-2.5 text-green-700 text-lg font-bold mt-0 mb-1">
          <CircleCheck className="w-5 h-5" />
          ¡Listo! {itemCount} {itemLabel} al carrito
        </h4>

        {isLocal && result.token_usage && (
          <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 py-1 px-2.5 rounded-full text-xs font-semibold">
            <Zap className="w-3 h-3" />
            {result.token_usage.tokens_used.toLocaleString()} tokens
            {' · '}
            {result.token_usage.tokens_remaining.toLocaleString()} restantes
          </div>
        )}
      </div>

      {/* Product list */}
      {itemCount > 0 && (
        <div className="bg-white rounded-lg shadow-sm mb-4 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {result.cart.map((item, index) => (
              <div key={`${item.product_id}-${item.format_id}`} className="flex items-center gap-3 px-4 py-3 group">
                <button
                  type="button"
                  onClick={() => onRemoveItem(index)}
                  className="flex-shrink-0 w-9 h-9 flex items-center justify-center border-none bg-gray-100 rounded-lg text-gray-400 cursor-pointer transition-all duration-150 hover:bg-red-50 hover:text-red-500"
                  title="Eliminar producto"
                >
                  <TrashIcon />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-800 font-medium text-sm m-0 truncate">
                    {item.product_name}
                  </p>
                  <p className="text-gray-400 text-xs m-0 mt-0.5">
                    {item.format_name && <span>{item.format_name}</span>}
                    {item.format_name && ' · '}
                    Cant: {item.quantity}
                    {item.price != null && (
                      <> · {formatPrice(item.price * item.quantity)}</>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col gap-2.5 md:flex-row md:flex-wrap">
        <div className="flex gap-2.5 max-md:w-full">
          <Link
            href={route('cart.review')}
            className="inline-flex items-center justify-center gap-1.5 bg-green-600 text-white py-2.5 px-6 rounded-md no-underline font-semibold text-sm transition-all hover:bg-green-700 hover:-translate-y-px hover:shadow-lg hover:shadow-green-600/30 hover:text-white max-md:flex-1 md:min-w-[180px]"
          >
            <ShoppingCart className="w-4 h-4 flex-shrink-0" />
            Ver Carrito
          </Link>

          {result.cart_share_url && (
            <button
              type="button"
              className="inline-flex items-center justify-center gap-1.5 bg-white text-green-600 border-2 border-green-600 py-2.5 px-6 rounded-md font-semibold text-sm cursor-pointer transition-all hover:bg-green-600 hover:text-white hover:-translate-y-px hover:shadow-lg hover:shadow-green-600/30 max-md:flex-1 md:min-w-[180px]"
              onClick={onShare}
            >
              <Share2 className="w-4 h-4 flex-shrink-0" />
              Compartir
            </button>
          )}
        </div>

        <button
          type="button"
          className="inline-flex items-center justify-center gap-1.5 bg-white text-green-600 border-2 border-green-600 py-2.5 px-6 rounded-md font-semibold text-sm cursor-pointer transition-all hover:bg-green-600 hover:text-white hover:-translate-y-px hover:shadow-lg hover:shadow-green-600/30 max-md:w-full md:min-w-[180px]"
          onClick={onNewList}
        >
          <WandSparkles className="w-4 h-4 flex-shrink-0" />
          Agregar más productos
        </button>
      </div>

      {isLocal && (
        <div className="bg-white rounded-lg p-5 shadow-sm mt-5">
          <h4 className="text-gray-800 mt-0 mb-4 text-base font-semibold">
            <Code2 className="inline w-4 h-4 mr-1 -mt-0.5" />
            Detalle de la Respuesta (Solo en desarrollo)
          </h4>
          <pre className="bg-gray-50 p-5 rounded-md border border-gray-200 max-h-[500px] overflow-auto text-[13px] m-0">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
